import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLines, Eyebrow, Counter, Marquee } from "@/components/Motion";
import { api } from "@/lib/api";
import { REGIONS, SOLUTIONS, INDUSTRIES, INSIGHTS, img } from "@/lib/content";

const DEFAULT_VIDEO =
  "https://videos.pexels.com/video-files/3121459/3121459-uhd_2560_1440_24fps.mp4";
const DEFAULT_POSTER = img("photo-1512453979798-5ea266f8880c", 1920);

const Hero = () => {
  const [media, setMedia] = useState({ video_url: DEFAULT_VIDEO, poster_url: DEFAULT_POSTER });
  const [videoFailed, setVideoFailed] = useState(false);
  useEffect(() => {
    api
      .get("/settings/public")
      .then((r) => {
        setMedia({
          video_url: r.data.video_url || DEFAULT_VIDEO,
          poster_url: r.data.poster_url || DEFAULT_POSTER,
        });
      })
      .catch(() => {});
  }, []);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.1]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen items-end overflow-hidden bg-obsidian"
      data-testid="home-hero"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        {media.video_url && !videoFailed ? (
          <video
            key={media.video_url}
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={(e) => e.currentTarget.play().catch(() => setVideoFailed(true))}
            onError={() => setVideoFailed(true)}
            poster={media.poster_url || undefined}
            data-testid="hero-video"
          >
            <source src={media.video_url} type="video/mp4" onError={() => setVideoFailed(true)} />
          </video>
        ) : (
          <img
            src={media.poster_url || DEFAULT_POSTER}
            alt="Dubai business district at dusk"
            className="kenburns h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/10 to-black/50" />
        <div className="absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-emerald-500/10 blur-[130px]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="nx-container relative pb-32 pt-48 lg:pb-40">
        <Reveal delay={0.15} y={16}>
          <Eyebrow>NEXREQ · UAE &amp; GCC</Eyebrow>
        </Reveal>
        <h1 className="mt-8 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
          <MaskedLines onLoad delay={0.35} lines={["Exceptional Talent.", "Precisely Placed."]} />
        </h1>
        <Reveal delay={0.95}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/70 lg:text-lg">
            NEXREQ connects ambitious organizations across the UAE and GCC with exceptional
            professionals through precision recruitment, executive search, RPO and talent advisory
            solutions.
          </p>
        </Reveal>
        <Reveal delay={1.1}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/hire-talent" className="nx-btn-primary" data-testid="hero-hire-talent-btn">
              Hire Talent <ArrowRight size={15} />
            </Link>
            <Link to="/jobs" className="nx-btn-outline" data-testid="hero-explore-btn">
              Explore Opportunities
            </Link>
            <Link
              to="/upload-resume"
              data-testid="hero-upload-resume-link"
              className="text-sm text-white/60 underline-offset-4 transition-colors hover:text-emerald-400 hover:underline"
            >
              Upload Your Resume
            </Link>
          </div>
        </Reveal>
      </motion.div>

      <div className="absolute bottom-8 right-8 hidden flex-col items-center gap-3 lg:flex">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 [writing-mode:vertical-lr]">
          Scroll
        </span>
        <span className="relative h-16 w-px overflow-hidden bg-white/15">
          <span className="scroll-dot absolute left-0 top-0 h-4 w-px bg-emerald-500" />
        </span>
      </div>
    </section>
  );
};

const TrustStrip = () => (
  <section className="border-y border-white/[0.08] bg-obsidian" data-testid="trust-strip">
    <div className="nx-container grid gap-10 py-14 lg:grid-cols-[1fr_2fr] lg:items-center">
      <Reveal>
        <Eyebrow>Global Reach. Regional Execution.</Eyebrow>
        <p className="mt-5 font-heading text-xl font-light leading-snug text-white/80">
          North American recruitment expertise.
          <br />
          GCC market execution.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {REGIONS.map((r) => (
            <span
              key={r}
              className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white/50"
            >
              <span className="h-1 w-1 rounded-full bg-emerald-500" />
              {r}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const EmployerChapter = () => (
  <section className="bg-obsidian py-28 lg:py-40" data-testid="employer-section">
    <div className="nx-container grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <Reveal>
            <Eyebrow>01 — For Employers</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl">
            <MaskedLines lines={["Build Teams That Move", "Your Business Forward."]} />
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-md leading-relaxed text-white/55">
              NEXREQ helps organizations identify, attract and secure high-performing talent across
              professional, technical, operational and leadership functions throughout the UAE and
              GCC.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <Link
              to="/employers"
              className="nx-btn-primary mt-10"
              data-testid="employer-solutions-btn"
            >
              Explore Employer Solutions <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-7">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.name} delay={i * 0.08}>
            <Link
              to="/solutions"
              data-testid={`solution-row-${i}`}
              className="group grid gap-3 border-t border-white/10 px-2 py-8 transition-colors duration-300 hover:bg-panel/[0.03] sm:grid-cols-[56px_1fr_32px] sm:items-start sm:gap-6"
            >
              <span className="pt-1 font-mono text-xs text-emerald-500/80">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>
                <span className="block font-heading text-xl font-light text-white transition-colors group-hover:text-emerald-400 lg:text-2xl">
                  {s.name}
                </span>
                <span className="mt-2 block max-w-lg text-sm leading-relaxed text-white/50">
                  {s.tagline}
                </span>
              </span>
              <ArrowUpRight
                className="mt-1 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-emerald-400"
                size={20}
              />
            </Link>
          </Reveal>
        ))}
        <div className="border-t border-white/10" />
      </div>
    </div>
  </section>
);

const ClientCta = () => (
  <section
    className="grain relative overflow-hidden border-y border-white/[0.08] bg-surface py-24 lg:py-32"
    data-testid="client-cta"
  >
    <div className="absolute right-0 top-0 h-72 w-72 rounded-full bg-emerald-500/[0.07] blur-[110px]" />
    <div className="nx-container relative grid gap-10 lg:grid-cols-[2fr_1fr] lg:items-end">
      <div>
        <Reveal>
          <Eyebrow>Start a Search</Eyebrow>
        </Reveal>
        <h2 className="mt-6 font-heading text-4xl font-light tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskedLines lines={["Have a Role to Fill?"]} />
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl leading-relaxed text-white/55">
            Tell us what you are hiring for. Our team will review your requirement and respond
            within one business day.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.3} className="lg:justify-self-end">
        <Link to="/hire-talent" className="nx-btn-primary" data-testid="client-cta-submit-btn">
          Submit a Hiring Requirement <ArrowRight size={15} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const CandidateChapter = ({ jobs }) => (
  <section className="bg-surface py-28 text-white lg:py-36" data-testid="candidate-section">
    <div className="nx-container">
      <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>02 — For Candidates</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
            <MaskedLines lines={["Your Next Role Should", "Move You Forward."]} />
          </h2>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <p className="leading-relaxed text-white/55">
              From senior appointments to career-defining moves across the Gulf, we represent
              professionals with discretion, honesty and genuine market insight.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/jobs" className="nx-btn-primary" data-testid="candidate-find-jobs-btn">
                Find Jobs <ArrowRight size={15} />
              </Link>
              <Link
                to="/upload-resume"
                className="nx-btn-outline"
                data-testid="candidate-upload-resume-btn"
              >
                Upload Your Resume
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
      {jobs.length > 0 && (
        <div className="mt-16 grid gap-px border border-white/[0.08] bg-white/[0.06] lg:grid-cols-3">
          {jobs.map((j) => (
            <Link
              key={j.id}
              to={`/jobs/${j.id}`}
              className="group bg-surface p-8 transition-colors duration-300 hover:bg-panel"
              data-testid={`featured-job-${j.id}`}
            >
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                <span>{j.reference}</span>
                <span className="text-emerald-400">{j.type}{j.demo ? " · Demo" : ""}</span>
              </div>
              <h3 className="mt-4 font-heading text-xl font-medium transition-colors group-hover:text-emerald-400">
                {j.title}
              </h3>
              <p className="mt-2 text-sm text-white/40">
                {j.location} · {j.sector}
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-[13px] font-medium">
                View Role
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  </section>
);

const STATS = [
  { to: 4, label: "Operating Regions" },
  { to: 9, label: "Markets Served" },
  { to: 8, label: "Core Sectors" },
  { to: 4, label: "Search Disciplines" },
];

const StatsBand = () => (
  <section className="border-b border-white/[0.08] bg-obsidian py-20 lg:py-24" data-testid="stats-band">
    <div className="nx-container grid grid-cols-2 gap-10 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1}>
          <div className="border-l border-white/10 pl-6">
            <div className="font-heading text-4xl font-light text-white lg:text-5xl">
              <Counter to={s.to} />
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              {s.label}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const IndustriesChapter = () => (
  <section className="bg-obsidian py-28 lg:py-36" data-testid="industries-section">
    <div className="nx-container">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-6">
          <Reveal>
            <Eyebrow>03 — Industries</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl">
            <MaskedLines lines={["Deep Specialization Across", "the Region's Defining Sectors."]} />
          </h2>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.2}>
            <p className="max-w-lg leading-relaxed text-white/55 lg:ml-auto">
              Sector fluency is the difference between a CV and a candidate. Our consultants operate
              inside the industries they serve.
            </p>
          </Reveal>
        </div>
      </div>
      <div className="mt-14 grid gap-px border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
        {INDUSTRIES.map((ind, i) => (
          <Link
            key={ind.name}
            to="/industries"
            className="group block h-full bg-obsidian p-8 transition-colors duration-300 hover:bg-surface"
            data-testid={`industry-tile-${i}`}
          >
            <span className="font-mono text-[10px] text-emerald-500/80">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-4 min-h-[3.5rem] font-heading text-lg font-light text-white transition-colors group-hover:text-emerald-400">
              {ind.name}
            </h3>
            <p className="mt-3 text-[13px] leading-relaxed text-white/45">{ind.blurb}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const InsightsPreview = () => (
  <section
    className="border-t border-white/[0.08] bg-obsidian py-28 lg:py-36"
    data-testid="insights-section"
  >
    <div className="nx-container">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <Reveal>
            <Eyebrow>04 — Insights</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl">
            <MaskedLines lines={["Intelligence for", "Decisive Leaders."]} />
          </h2>
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/insights"
            className="group inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-emerald-400"
            data-testid="insights-view-all-link"
          >
            View all insights
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-10 md:grid-cols-3">
        {INSIGHTS.slice(0, 3).map((a, i) => (
          <Reveal key={a.title} delay={i * 0.1}>
            <Link to="/insights" className="group block" data-testid={`insight-card-${i}`}>
              <div className="overflow-hidden">
                <img
                  src={img(a.image, 900)}
                  alt={a.title}
                  className="aspect-[4/3] w-full object-cover grayscale-[35%] transition duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  loading="lazy"
                />
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-500">
                {a.tag}
              </p>
              <h3 className="mt-3 font-heading text-xl font-light leading-snug text-white transition-colors group-hover:text-emerald-400">
                {a.title}
              </h3>
              <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-white/45">{a.excerpt}</p>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
                {a.date} · {a.readTime}
              </p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section
    className="grain relative overflow-hidden border-t border-white/[0.08] bg-surface py-28 lg:py-36"
    data-testid="final-cta"
  >
    <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-emerald-500/[0.06] blur-[130px]" />
    <div className="nx-container relative">
      <Reveal>
        <Eyebrow>NEXREQ UAE &amp; GCC</Eyebrow>
      </Reveal>
      <h2 className="mt-8 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-7xl">
        <MaskedLines lines={["Connecting Exceptional Talent", "With Exceptional Organizations."]} />
      </h2>
      <div className="mt-12 flex flex-wrap gap-4">
        <Reveal delay={0.2}>
          <Link to="/hire-talent" className="nx-btn-primary" data-testid="final-cta-hire-btn">
            Start a Conversation <ArrowRight size={15} />
          </Link>
        </Reveal>
        <Reveal delay={0.3}>
          <Link to="/jobs" className="nx-btn-outline" data-testid="final-cta-jobs-btn">
            Explore Opportunities
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api
      .get("/jobs")
      .then((r) => {
        const jobs = r.data.jobs || [];
        const f = jobs.filter((j) => j.featured);
        setFeatured((f.length >= 3 ? f : jobs).slice(0, 3));
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <Seo
        title="NEXREQ UAE & GCC — Executive Search & Talent Solutions"
        siteName="NEXREQ"
        description="NEXREQ connects ambitious organizations across the UAE and GCC with exceptional professionals through precision recruitment, executive search, RPO and talent advisory."
        image="/brand/nexreq-logo.webp"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "NEXREQ UAE & GCC",
          url: window.location.origin,
          email: "info@nexreq.ae",
          slogan: "Your People Partner in Every Hire.",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Bin Dasmal Building – 1, Al Quoz Industrial First",
            addressLocality: "Dubai",
            addressCountry: "AE",
          },
        }}
      />
      <Hero />
      <TrustStrip />
      <EmployerChapter />
      <ClientCta />
      <Marquee
        className="border-b border-white/[0.08] bg-obsidian py-6 font-heading text-xl font-light uppercase tracking-[0.2em] text-white/20 lg:text-2xl"
        items={[
          "Executive Search",
          "Direct Hire",
          "Recruitment Process Outsourcing",
          "Talent Advisory",
          "GCC Talent Solutions",
          "Professional Search",
        ]}
      />
      <CandidateChapter jobs={featured} />
      <StatsBand />
      <IndustriesChapter />
      <InsightsPreview />
      <FinalCta />
    </>
  );
}
